import { Component, Inject, OnDestroy, OnInit, computed, input, signal } from '@angular/core';
import { AdminsApi, AdminSummary } from 'src/app/hickory-api';
import { AuthService } from '../authentication/auth.service';
import { L10N_LOCALE, L10nLocale, L10nTranslationService } from 'angular-l10n';
import { catchError, finalize, Subject, takeUntil, tap, throwError } from 'rxjs';
import { ApiError } from '../model/utility';
import { SB_PANEL_ERROR } from '../snackbar.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { AdminSelectDialogComponent } from '../admin-select-dialog/admin-select-dialog.component';
import { ApiConfig } from '../api-config/api-config';

@Component({
  selector: 'app-admin-form',
  templateUrl: './admin-form.component.html',
  styleUrl: './admin-form.component.scss',
  standalone: false
})
export class AdminFormComponent implements OnInit, OnDestroy {
  readonly readonly = input<boolean>(false);
  readonly form = input<FormControl<number>>(undefined as unknown as FormControl<number>);
  readonly tenantId = input<number>(+ApiConfig.tenantId);
  readonly label = input<string>(undefined as unknown as string);
  readonly hint = input<string>(undefined as unknown as string);

  selectedAdmin = signal<AdminSummary | null>(null);
  displayValue = computed(() => this.computeDisplayValue(this.selectedAdmin()));
  adminLoading = signal(false);
  destroy$ = new Subject<void>();
  displayControl = new FormControl('');

  constructor(
    private readonly authService: AuthService,
    private readonly dialog: MatDialog,
    private readonly adminApi: AdminsApi,
    private readonly sb: MatSnackBar,
    private readonly translation: L10nTranslationService,
    @Inject(L10N_LOCALE) public locale: L10nLocale,
  ) {}

  ngOnInit(): void {
    this.getAdmin();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onAdminSelect() {
    const dialog = this.dialog.open(AdminSelectDialogComponent, {
      minWidth: '500px',
      data: {
        tenantId: this.tenantId(),
      },
    });

    dialog.afterClosed().subscribe((result: AdminSummary | null) => {
      if (result) {
        this.form().setValue(result.id!);
        this.selectedAdmin.set(result);
        this.updateDisplayValue();
      }
    });
  }

  getAdmin() {
    this.adminLoading.set(true);
    const currentUser = this.authService.user.snapshot;
    const currentTenantId = this.tenantId() || currentUser?.tenantId;
    const senderId = this.form().value || currentUser?.id;

    if (senderId === currentUser?.id && currentUser?.tenantId === currentTenantId) {
      this.selectedAdmin.set({
        id: senderId,
        firstName: currentUser?.name?.givenName,
        lastName: currentUser?.name?.familyName,
      });
      this.updateDisplayValue();
      this.form().setValue(senderId!);
      this.adminLoading.set(false);
      return;
    }

    this.adminApi
      .getAdminUsers(1, 25, undefined, [senderId!], undefined, undefined, undefined, undefined, undefined, currentTenantId)
      .pipe(
        tap((response) => {
          if (response?.items?.length) {
            this.selectedAdmin.set(response?.items[0] as AdminSummary);
            this.updateDisplayValue();
          }
        }),
        catchError((error: ApiError) => {
          const message = this.translation.translate('Integrations.FetchDataFailed');
          const action = this.translation.translate('LoadingSpinner.Retry');
          const sbRef = this.sb.open(message, action, { duration: 10000, panelClass: SB_PANEL_ERROR });
          sbRef.onAction().subscribe(() => {
            this.getAdmin();
          });

          return throwError(() => error);
        }),
        takeUntil(this.destroy$),
        finalize(() => {
          this.adminLoading.set(false);
        }),
      )
      .subscribe();
  }

  getLabel(): string {
    return this.label() || this.translation.translate('Select Administrator');
  }

  private computeDisplayValue(admin: AdminSummary | null) {
    let value = '';
    if (!admin) {
      value = 'Unassigned';
      return value;
    }

    if (!admin.lastName) {
      value = admin.firstName ?? '';
    } else {
      value = admin.lastName + ', ' + admin.firstName;
    }

    return value;
  }

  private updateDisplayValue() {
    this.displayControl.setValue(this.displayValue(), { emitEvent: false });
  }
}
